import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "@/components/ThemeProvider";
import { useAuth } from "@/components/AuthProvider";
import { Sun, Moon, Search, LogOut, User as UserIcon, Menu, Settings, CreditCard, HelpCircle } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import SprintrollIcon from "@/components/SprintrollIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationsBell from "@/components/NotificationsBell";
import SprintrollLogo from "@/components/SprintrollLogo";
import { useTenant } from "@/components/TenantProvider";

export default function Header({ onMenuClick }) {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="h-16 px-4 md:px-6 glass-header flex items-center gap-3 z-30 relative">
      <button
        className="md:hidden h-9 w-9 rounded-lg grid place-items-center hover:bg-white/40 dark:hover:bg-white/10"
        onClick={onMenuClick}
        data-testid="header-menu-button"
        aria-label="Menú"
      >
        <Menu className="h-5 w-5" />
      </button>
      <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
        {tenant?.branding?.logo_url && (
          <>
            <img
              src={tenant.branding.logo_url}
              alt={tenant.name || tenant.slug}
              className="h-8 w-auto max-w-[160px] object-contain"
              data-testid="header-tenant-logo"
            />
            <span className="h-6 w-px bg-slate-300 dark:bg-slate-600" />
          </>
        )}
        <SprintrollLogo className="h-7 w-auto" />
      </Link>

      <div className="flex-1 max-w-md mx-auto hidden md:block">
        <GlobalSearch />
      </div>

      <div className="ml-auto flex items-center gap-1">
        <LanguageSwitcher />
        <NotificationsBell />
        <button
          onClick={toggle}
          data-testid="theme-toggle"
          className="h-9 w-9 rounded-full grid place-items-center hover:bg-white/40 dark:hover:bg-white/10 transition"
          aria-label={t("nav.toggleTheme")}
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-button" className="h-9 w-9 rounded-full grid place-items-center hover:bg-white/40 dark:hover:bg-white/10 transition">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback>{user?.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 glass-panel border-white/40 dark:border-white/10">
            <DropdownMenuLabel className="flex flex-col">
              <span className="font-medium">{user?.name}</span>
              <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
              <Settings className="h-4 w-4 mr-2" /> {t("nav.settings")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/help")} data-testid="menu-help">
              <HelpCircle className="h-4 w-4 mr-2" /> {t("nav.help")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-pricing">
              <CreditCard className="h-4 w-4 mr-2" /> {t("landing.topnav.pricing")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-rose-500">
              <LogOut className="h-4 w-4 mr-2" /> {t("nav.signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
